import { useCallback, useMemo, useState } from "react";

import { useChildren } from "@/features/selectedChild";
import { getChildLabel } from "@/features/selectedChild/getChildLabel";
import { showChildLabels as shouldShowChildLabels } from "@/features/selectedChild/showChildLabels";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { formatIsoDate } from "@/lib/date";
import { useGetLeaveRequestsForChildrenQuery } from "@/query/apiSlice";

import {
    filterLeaveRequestsByChild,
    getLeaveRequestOverviewWindow,
    splitLeaveRequestsForOverview,
} from "./groupLeaveRequests";

/**
 * Data and derived state for the submissions screen:
 * loads leave requests for all children and splits them into
 * action-needed and period sections for the selected window.
 */
export function useSubmissionsScreen() {
    const {
        children,
        isLoading: isLoadingChildren,
        refetch: refetchChildren,
    } = useChildren();
    const childIds = useMemo(
        () => children.map((child) => child.id),
        [children],
    );
    const showChildLabels = shouldShowChildLabels(children.length);
    const todayIso = formatIsoDate(new Date());

    const [windowShift, setWindowShift] = useState(0);
    const [childId, setChildId] = useState<number | null>(null);

    const {
        data: leaveRequests = [],
        isLoading: isLoadingRequests,
        refetch: refetchLeaveRequests,
    } = useGetLeaveRequestsForChildrenQuery(childIds, {
        skip: childIds.length === 0,
    });

    const onRefreshData = useCallback(
        () => Promise.all([refetchChildren(), refetchLeaveRequests()]),
        [refetchChildren, refetchLeaveRequests],
    );
    const { refreshControl } = usePullToRefresh(onRefreshData);

    const childrenById = useMemo(() => {
        const map = new Map<number, string>();
        for (const child of children) {
            map.set(child.id, getChildLabel(child));
        }
        return map;
    }, [children]);

    const dateWindow = useMemo(
        () => getLeaveRequestOverviewWindow(todayIso, windowShift),
        [todayIso, windowShift],
    );

    const filteredRequests = useMemo(
        () => filterLeaveRequestsByChild(leaveRequests, childId),
        [childId, leaveRequests],
    );

    const sections = useMemo(
        () =>
            splitLeaveRequestsForOverview(filteredRequests, {
                windowStartIso: dateWindow.startIso,
                windowEndIso: dateWindow.endIso,
            }),
        [dateWindow.endIso, dateWindow.startIso, filteredRequests],
    );

    return {
        children,
        childrenById,
        showChildLabels,
        sections,
        dateWindow,
        windowShift,
        setWindowShift,
        childId,
        setChildId,
        isLoading: isLoadingChildren || isLoadingRequests,
        refreshControl,
    };
}
